import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { api } from "@/services/api/client";
import type {
  TokenVault,
  VaultAssignment,
  VaultUsage,
  CreditBalance,
  CreditTransaction,
  Invoice,
  PaginationMeta,
  VaultType,
} from "@/types/billing";

/**
 * Vault Store
 * Manages token vaults, vault assignments, credit balance, and usage tracking.
 * Follows the same subscribeWithSelector + immer pattern as organization-store.
 */

interface CreateVaultData {
  name: string;
  type: VaultType;
  allocatedBudgetCents: number;
  allowFallthrough?: boolean;
}

interface UpdateVaultData {
  name?: string;
  allocatedBudgetCents?: number;
  allowFallthrough?: boolean;
}

interface AssignToVaultData {
  targetType: VaultAssignment["targetType"];
  targetId: string;
  priority?: number;
}

const TRUSTED_STRIPE_REDIRECT_HOSTS = new Set(["checkout.stripe.com"]);

function getTrustedStripeRedirectUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && TRUSTED_STRIPE_REDIRECT_HOSTS.has(url.hostname)
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

interface VaultState {
  // State
  vaults: TokenVault[];
  myUsage: VaultUsage[];
  creditBalance: CreditBalance | null;
  transactions: CreditTransaction[];
  transactionsPagination: PaginationMeta | null;
  invoices: Invoice[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchVaults: () => Promise<void>;
  createVault: (data: CreateVaultData) => Promise<TokenVault>;
  updateVault: (id: string, data: UpdateVaultData) => Promise<TokenVault>;
  deleteVault: (id: string) => Promise<void>;
  assignToVault: (vaultId: string, data: AssignToVaultData) => Promise<void>;
  removeFromVault: (vaultId: string, targetId: string) => Promise<void>;
  fetchMyUsage: () => Promise<void>;
  fetchCreditBalance: () => Promise<void>;
  purchaseCredits: (packId: string) => Promise<void>;
  fetchTransactions: (filters?: { page?: number; limit?: number; type?: string; vaultId?: string }) => Promise<void>;
  fetchInvoices: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export const useVaultStore = create<VaultState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // Initial state
      vaults: [],
      myUsage: [],
      creditBalance: null,
      transactions: [],
      transactionsPagination: null,
      invoices: [],
      isLoading: false,
      error: null,

      // Fetch all vaults for the active organization
      fetchVaults: async () => {
        if (get().isLoading) return;

        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.get<TokenVault[]>("/billing/vaults");

          set((state) => {
            state.vaults = response.data || [];
            state.isLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to fetch vaults";
            state.isLoading = false;
          });
        }
      },

      // Create a new token vault
      createVault: async (data: CreateVaultData) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.post<TokenVault, CreateVaultData>(
            "/billing/vaults",
            data,
          );

          const newVault = response.data;
          if (!newVault) {
            throw new Error("Failed to create vault: no data returned");
          }

          set((state) => {
            state.vaults.push(newVault);
            state.isLoading = false;
          });

          return newVault;
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to create vault";
            state.isLoading = false;
          });
          throw error;
        }
      },

      // Update an existing vault
      updateVault: async (id: string, data: UpdateVaultData) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.patch<TokenVault, UpdateVaultData>(
            `/billing/vaults/${id}`,
            data,
          );

          const updatedVault = response.data;
          if (!updatedVault) {
            throw new Error("Failed to update vault: no data returned");
          }

          set((state) => {
            const index = state.vaults.findIndex((v) => v.id === id);
            if (index !== -1) {
              state.vaults[index] = updatedVault;
            }
            state.isLoading = false;
          });

          return updatedVault;
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to update vault";
            state.isLoading = false;
          });
          throw error;
        }
      },

      // Delete a vault
      deleteVault: async (id: string) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          await api.delete(`/billing/vaults/${id}`);

          set((state) => {
            state.vaults = state.vaults.filter((v) => v.id !== id);
            state.isLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to delete vault";
            state.isLoading = false;
          });
          throw error;
        }
      },

      // Assign a target (user, group, app, agent) to a vault
      assignToVault: async (vaultId: string, data: AssignToVaultData) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          await api.post<void, AssignToVaultData>(
            `/billing/vaults/${vaultId}/assign`,
            data,
          );

          // Refresh vaults to get updated assignments
          set((state) => {
            state.isLoading = false;
          });

          await get().fetchVaults();
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to assign to vault";
            state.isLoading = false;
          });
          throw error;
        }
      },

      // Remove a target from a vault
      removeFromVault: async (vaultId: string, targetId: string) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          await api.delete(`/billing/vaults/${vaultId}/assign/${targetId}`);

          // Refresh vaults to get updated assignments
          set((state) => {
            state.isLoading = false;
          });

          await get().fetchVaults();
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to remove from vault";
            state.isLoading = false;
          });
          throw error;
        }
      },

      // Fetch usage data for the current user
      fetchMyUsage: async () => {
        try {
          const response = await api.get<VaultUsage[]>("/billing/vaults/my-usage");

          set((state) => {
            state.myUsage = response.data || [];
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to fetch usage data";
          });
        }
      },

      // Fetch extra credit balance
      fetchCreditBalance: async () => {
        try {
          const response = await api.get<CreditBalance>("/billing/credits/balance");

          set((state) => {
            state.creditBalance = response.data || null;
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to fetch credit balance";
          });
        }
      },

      // Purchase extra credits via Stripe Checkout and redirect
      purchaseCredits: async (packId: string) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.post<
            { url: string },
            { creditPackId: string; successUrl: string; cancelUrl: string }
          >("/billing/credits/purchase", {
            creditPackId: packId,
            successUrl: `${window.location.origin}/billing?credits=success`,
            cancelUrl: `${window.location.origin}/billing`,
          });

          set((state) => {
            state.isLoading = false;
          });

          const redirectUrl = response.data?.url
            ? getTrustedStripeRedirectUrl(response.data.url)
            : null;
          if (redirectUrl) {
            window.location.href = redirectUrl;
          } else {
            throw new Error("Invalid billing redirect URL");
          }
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to purchase credits";
            state.isLoading = false;
          });
          throw error;
        }
      },

      // Fetch paginated transaction history
      fetchTransactions: async (filters?: { page?: number; limit?: number; type?: string; vaultId?: string }) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const params = new URLSearchParams();
          if (filters?.page) params.set("page", String(filters.page));
          if (filters?.limit) params.set("limit", String(filters.limit));
          if (filters?.type) params.set("type", filters.type);
          if (filters?.vaultId) params.set("vaultId", filters.vaultId);

          const qs = params.toString();
          const response = await api.get<{ data: CreditTransaction[]; pagination: PaginationMeta }>(
            `/billing/transactions${qs ? `?${qs}` : ""}`,
          );

          set((state) => {
            state.transactions = response.data?.data || [];
            state.transactionsPagination = response.data?.pagination || null;
            state.isLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to fetch transactions";
            state.isLoading = false;
          });
        }
      },

      // Fetch invoice history
      fetchInvoices: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.get<Invoice[]>(
            "/billing/invoices",
          );

          set((state) => {
            state.invoices = response.data || [];
            state.isLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to fetch invoices";
            state.isLoading = false;
          });
        }
      },

      // Clear error state
      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },

      // Reset store (on logout or org switch)
      reset: () => {
        set((state) => {
          state.vaults = [];
          state.myUsage = [];
          state.creditBalance = null;
          state.transactions = [];
          state.transactionsPagination = null;
          state.invoices = [];
          state.isLoading = false;
          state.error = null;
        });
      },
    })),
  ),
);

// Subscribe to auth store to reset vault store on logout
if (typeof window !== "undefined") {
  import("../auth/auth-store").then(({ useAuthStore }) => {
    useAuthStore.subscribe(
      (state) => state.isAuthenticated,
      (isAuthenticated, previousIsAuthenticated) => {
        if (previousIsAuthenticated && !isAuthenticated) {
          useVaultStore.getState().reset();
        }
      },
    );
  });
}
